import { useCallback, useMemo } from 'react';

import { Building2, Contact, FileStackIcon, NotebookPenIcon } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import { FormDateInput, FormField, FormInput, FormSelect } from '@/_components';
import type { QuickItemRenderer } from '@/_containers';
import { CardContainer, FormCurrencyInput, FormFilesInput, FormLayout, SectionHeading } from '@/_containers';
import { RichText } from '@/_containers/inputs/rich-text';
import { CompanyMultiSelect } from '@/_pages/companies/shared/components/multi-select';
import { CompanyItem } from '@/_pages/contacts/profile/components';
import { SyncContactMultiSelect } from '@/_pages/contacts/shared';
import type { CompanyQueryLinkerWithPivotSchema } from '@/_schemas/companies/company-query-linker-with-pivot-schema';
import { allowedFileTypes, showFormField } from '@/_shared';

import type { CompanyFilteredSchema, MarketingCampaignFormSchema } from '../schemas';
import { campaignTypesOptions } from '../schemas';

export function FormFields({
  id,
  form,
  isEditable = true,
  autoFocus = false,
}: {
  id: string;
  form: UseFormReturn<MarketingCampaignFormSchema>;
  isEditable?: boolean;
  autoFocus?: boolean;
}) {
  const folder_path = `marketing-campaign/${id}`;
  const [notes, attached_files, clients, contacts] = form.watch(['notes', 'attached_files', 'clients', 'contacts']);

  const { uniqueClientContacts, filteredContacts } = useMemo(() => {
    const uniqueContactsMap = new Map();
    (
      clients?.reduce(
        (accumulator, client) => {
          if (client.contacts && accumulator !== null && Array.isArray(client.contacts)) {
            return [...accumulator, ...client.contacts];
          }
          return accumulator;
        },
        [] as CompanyFilteredSchema['contacts']
      ) ?? []
    ).forEach((contact) => {
      if (contact.id) {
        uniqueContactsMap.set(contact.id, contact);
      }
    });

    const uniqueClientContacts = Array.from(uniqueContactsMap.values());

    // Filter contacts to only include those available in uniqueClientContacts
    const filteredContacts =
      contacts?.filter((contact) => uniqueClientContacts.some((uniqueContact) => uniqueContact.id === contact.id)) ??
      [];

    return { uniqueClientContacts, filteredContacts };
  }, [clients, contacts]);

  const renderCompany: QuickItemRenderer<CompanyQueryLinkerWithPivotSchema> = useCallback(
    (item, { index, isEditable, removeItem }) => (
      <CompanyItem
        key={`${item.id}-${index}`}
        company={item}
        isEditable={isEditable}
        canSetPrimary={false}
        removeItemHandler={removeItem}
      />
    ),
    []
  );

  return (
    <>
      <CardContainer>
        <FormLayout>
          <FormField
            control={form.control}
            name="name"
            render={({ field: { value, onChange } }) => (
              <FormInput
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                label="Campaign Name (required)"
                value={value}
                onChange={onChange}
                disabled={!isEditable}
                autoFocus={autoFocus}
              />
            )}
          />

          <FormField
            control={form.control}
            name="campaign_type"
            render={({ field: { value, onChange } }) => (
              <FormSelect
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                disabled={!isEditable}
                value={value}
                onChange={onChange}
                options={campaignTypesOptions}
                getOptionLabel={(option) => option.name}
                label="Type"
              />
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field: { value, onChange } }) => (
              <FormDateInput
                disabled={!isEditable}
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
                value={value}
                onValueChange={onChange}
                label="Date"
              />
            )}
          />

          <FormCurrencyInput
            isEditable={isEditable}
            label="Cost"
            name="total_cost_of_campaign"
            formItemClassName="col-span-12 sm:col-span-6 lg:col-span-4"
          />
        </FormLayout>
      </CardContainer>

      <div className="flex flex-col gap-4 lg:flex-row">
        <CardContainer className="flex-1">
          <CompanyMultiSelect
            companies={clients || []}
            control={form.control}
            fieldName="clients"
            isEditable={isEditable}
            icon={Building2}
            renderItem={renderCompany}
            canSetPrimary={false}
            apiParams={{ include: 'contacts.profile_image' }}
          />
        </CardContainer>
        <CardContainer className="flex-1">
          <SyncContactMultiSelect
            options={uniqueClientContacts}
            items={filteredContacts}
            control={form.control}
            fieldName="contacts"
            isEditable={isEditable}
            sectionTitle="Contacts"
            title="Contacts"
            icon={Contact}
          />
        </CardContainer>
      </div>

      {showFormField(isEditable, notes.trim()) && (
        <CardContainer>
          <SectionHeading text="Notes" icon={NotebookPenIcon} />
          <FormLayout className="mt-8">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <RichText
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!isEditable}
                  inline={!isEditable}
                  mediaFolderPath={folder_path}
                  className="col-span-12"
                />
              )}
            />
          </FormLayout>
        </CardContainer>
      )}

      {showFormField(isEditable, (attached_files?.length ?? []) > 0) && (
        <CardContainer>
          <SectionHeading text="Documents" icon={FileStackIcon} />

          <FormField
            control={form.control}
            name="attached_files"
            render={({ field: { value, onChange } }) => (
              <FormFilesInput
                folder_path={folder_path}
                value={value}
                disabled={!isEditable}
                onChange={onChange}
                formItemClassName="mt-6"
                allowedTypes={allowedFileTypes.all}
              />
            )}
          />
        </CardContainer>
      )}
    </>
  );
}
